import React, { useEffect, useState, useRef } from "react";
import { useFormik } from "formik";
import { Link, useNavigate } from "react-router-dom";
import { InputText } from "primereact/inputtext";
import { FloatLabel } from "primereact/floatlabel";
import { Message } from "primereact/message";
import { Button } from "primereact/button";
import { Password } from "primereact/password";
import { Toast } from "primereact/toast";

import {
  initialValues,
  loginValidationSchema,
  LoginValues,
} from "../validation/LoginValidation";
import { AuthService } from "../services/authService";

const LoginPage = () => {
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const toastRef = useRef<Toast>(null);

  const handleSubmit = async (values: LoginValues) => {
    try {
      setSubmitting(true);
      const res = await AuthService.loginUser(values);
      if (res.status === 2000) {
        toastRef.current?.show({
          severity: "success",
          summary: "Success",
          detail: res.message,
          life: 3000,
        });
        await AuthService.sendOtp(values.email);
        navigate("/auth/otp", { state: { email: values.email } });
      } else {
        toastRef.current?.show({
          severity: "warn",
          summary: "Error",
          detail: res.message,
          life: 3000,
        });
      }
    } catch (err: any) {
      const error = err.response?.data?.message;
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: error,
        life: 3000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: loginValidationSchema,
    onSubmit: handleSubmit,
  });

  return (
    <div>
      <Toast ref={toastRef} className="toast-top-center" />
      <div className="login">
        <div className="flex flex-col items-center mt-5">
          <h4 className="text-xl mb-1 font-bold">Login to your Account</h4>
          <p className="mb-6 text-slate-500 text-sm">
            Welcome back! Please enter your details.
          </p>
        </div>
        <form onSubmit={formik.handleSubmit}>
          <div className="grid grid-cols-1 w-full md:grid-cols-2 gap-2">
            <div className="field  mb-4 col-span-2">
              <FloatLabel>
                <InputText
                  id="email"
                  name="email"
                  type="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  className={`w-full p-2 border rounded-md ${formik.errors.email && formik.touched.email ? "p-invalid border-red-500" : "border-gray-300"}`}
                  placeholder=" "
                />
                <label htmlFor="email">Email *</label>
              </FloatLabel>
              {formik.errors.email && formik.touched.email && (
                <Message
                  severity="error"
                  text={formik.errors.email}
                  className="input-message"
                />
              )}
            </div>

            <div className="logI flex flex-col w-full col-span-2">
              <FloatLabel>
                <Password
                  id="password"
                  name="password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  className={`p-2 border rounded-md w-full ${formik.errors.password && formik.touched.password ? "p-invalid border-red-500" : "border-gray-300"}`}
                  toggleMask
                  showIcon="pi pi-eye"
                  hideIcon="pi pi-eye-slash"
                  feedback={false}
                />
                <label htmlFor="password">Password *</label>
              </FloatLabel>
              {formik.errors.password && formik.touched.password && (
                <Message
                  severity="error"
                  text={formik.errors.password}
                  className="input-message"
                />
              )}
            </div>

            <div className="text-left col-span-2 mb-2">
              <p className="text-sm">
                Forgot password ?{" "}
                <Link
                  to={"/auth/forgot-password"}
                  className="text-primary-600 fw-semibold"
                >
                  Reset
                </Link>
              </p>
            </div>

            <Button
              label="Login"
              type="submit"
              loading={submitting}
              className="w-full col-span-2 p-2 font-bold text-xl rounded bg-primary-500 text-white hover:bg-orange-600"
              disabled={submitting || !formik.isValid}
            />
          </div>
        </form>
        <div className="mt-2 text-center text-sm">
          <p className="mb-0">
            Don't have an account?{" "}
            <Link
              to={"/auth/register"}
              className="text-primary-600 fw-semibold"
            >
              Sign Up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
